import type { MatchConfig, PresenceStatus } from '@moba/shared';
import { EN } from './i18n.en.ts';

/**
 * Idioma de la interfaz. Inglés por defecto; español desde Configuración
 * (se guarda en `localStorage` y la página se recarga al cambiarlo, así que el
 * idioma es fijo durante toda la sesión y las tablas de textos se resuelven al
 * cargar).
 *
 * Las cadenas fuente están en español y se usan como clave: `t('Volver al menú')`
 * devuelve "Back to menu" en inglés. Los parámetros van entre llaves:
 * `t('Nivel {level}', { level: 3 })`. El diccionario español→inglés está en
 * `i18n.en.ts`. Los mensajes del servidor llegan codificados con `msg()` de
 * shared (`i18n:["plantilla", {params}]`) y se traducen con `tr()`.
 */

export type Lang = 'en' | 'es';

const STORAGE_KEY = 'moba.lang';

function storedLang(): Lang {
  try {
    const forced = new URLSearchParams(window.location.search).get('lang');
    if (forced === 'es' || forced === 'en') return forced;
    return localStorage.getItem(STORAGE_KEY) === 'es' ? 'es' : 'en';
  } catch {
    return 'en';
  }
}

export const LANG: Lang = storedLang();

/** Guarda el idioma (se aplica al recargar). */
export function saveLanguage(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // sin almacenamiento: no se puede recordar
  }
}

type Params = Record<string, string | number>;

const missing = new Set<string>();

function fill(text: string, params?: Params): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (whole, key: string) => (key in params ? String(params[key]) : whole));
}

/** Traduce una cadena fuente (en español) al idioma actual. */
export function t(text: string, params?: Params): string {
  if (LANG === 'es') return fill(text, params);
  const english = EN[text];
  if (english === undefined && /[a-záéíóúñ]/i.test(text) && !missing.has(text)) {
    missing.add(text);
    (window as unknown as { __i18nMissing?: string[] }).__i18nMissing = [...missing];
  }
  return fill(english ?? text, params);
}

/**
 * Traduce un mensaje que puede venir codificado del servidor
 * (`i18n:["plantilla", {params}]`, ver `msg()` en shared); si no lo está, lo
 * devuelve tal cual (nombres, texto de jugadores…).
 */
export function tr(message: string | undefined | null): string {
  if (!message) return '';
  const index = message.indexOf('i18n:');
  if (index < 0) return message;
  try {
    const [template, params] = JSON.parse(message.slice(index + 5)) as [string, Params?];
    // los parámetros que a su vez son mensajes codificados también se traducen
    const resolved: Params = {};
    for (const [key, value] of Object.entries(params ?? {})) resolved[key] = typeof value === 'string' ? tr(value) : value;
    return message.slice(0, index) + t(template, resolved);
  } catch {
    return message;
  }
}

/**
 * Traduce el HTML estático: los nodos de texto y los atributos `title`,
 * `placeholder` y `aria-label` cuyo contenido (sin espacios de los bordes)
 * esté en el diccionario.
 */
export function translateDom(root: ParentNode = document.body): void {
  document.documentElement.lang = LANG;
  if (LANG === 'es') return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const raw = node.nodeValue ?? '';
    const text = raw.trim().replace(/\s+/g, ' ');
    if (!text) continue;
    const english = EN[text];
    if (english !== undefined) node.nodeValue = raw.replace(raw.trim(), english);
  }
  const elements = root instanceof Element ? [root, ...root.querySelectorAll('*')] : [...root.querySelectorAll('*')];
  for (const element of elements) {
    for (const attribute of ['title', 'placeholder', 'aria-label']) {
      const value = element.getAttribute(attribute);
      if (value && EN[value] !== undefined) element.setAttribute(attribute, EN[value]);
    }
  }
}

/** "3v3 · Tres líneas" / "ARAM 2v2" en el idioma actual. */
export function matchLabelT(config: MatchConfig): string {
  const size = `${config.teamSize}v${config.teamSize}`;
  return config.mode === 'aram' ? `ARAM ${size}` : t('{size} · Tres líneas', { size });
}

/** Código neutro del modo (lo que viaja en la presencia y las invitaciones). */
export function modeCode(config: MatchConfig | 'training'): string {
  return config === 'training' ? 'training' : `${config.mode}:${config.teamSize}`;
}

/** Texto de un código de modo en el idioma de quien lo lee ("ARAM 2v2", "3v3 líneas", "Entrenamiento"). */
export function modeCodeLabel(code: string | undefined): string {
  if (!code) return '';
  if (code === 'training') return t('Entrenamiento');
  const [mode, size] = code.split(':');
  const n = Number(size);
  if (!Number.isFinite(n)) return code;
  return mode === 'aram' ? `ARAM ${n}v${n}` : t('{size} líneas', { size: `${n}v${n}` });
}

/** Estado de un amigo ("En partida · ARAM 2v2 · Link"). */
export function presenceLabelT(status: PresenceStatus | undefined, heroName?: (id: string) => string): string {
  if (!status) return t('Desconectado');
  const hero = status.heroId && heroName ? heroName(status.heroId) : '';
  const mode = modeCodeLabel(status.mode);
  const parts = (...items: (string | undefined)[]) => items.filter(Boolean).join(' · ');
  switch (status.state) {
    case 'lobby':
      return parts(t('En una sala'), mode);
    case 'match':
      return parts(t('En partida'), mode, hero);
    case 'offline-match':
      return parts(t('Jugando offline'), mode, hero);
    default:
      return t('En el menú');
  }
}

/** Nombre visible de un héroe: el del jugador local offline se traduce ("You"/"Tú"). */
export function heroDisplayName(name: string): string {
  return name === 'You' ? t('Tú') : name;
}
